/**
 * Demo Worlds System
 * 7 Pre-built VR environments that randomly load at startup
 */

export const DemoWorlds = {
    worlds: [
        {
            id: 'beach-sunset',
            name: 'Beach Sunset',
            description: 'Relaxing beach with palm trees and sunset sky',
            environment: {
                preset: 'default',
                skyType: 'gradient',
                skyColor: '#FF6B35',
                horizonColor: '#FFA07A',
                ground: 'flat',
                groundColor: '#F4D03F',
                groundTexture: 'walkernoise',
                lighting: 'distant',
                lightPosition: { x: -1, y: 1, z: -2 },
                fog: 0.6
            },
            objects: [
                { type: 'sphere', position: { x: 0, y: 2, z: -5 }, scale: { x: 2, y: 2, z: 2 }, color: '#FFA500', animation: 'float' },
                { type: 'box', position: { x: -3, y: 0.5, z: -4 }, scale: { x: 1, y: 1, z: 1 }, color: '#8B4513', rotation: { x: 0, y: 45, z: 0 } },
                { type: 'cylinder', position: { x: 3, y: 1, z: -4 }, scale: { x: 0.3, y: 3, z: 0.3 }, color: '#654321' },
                { type: 'cone', position: { x: 3, y: 4, z: -4 }, scale: { x: 1.5, y: 1.5, z: 1.5 }, color: '#228B22' }
            ],
            lights: [
                { type: 'point', position: { x: -5, y: 3, z: -3 }, color: '#FFD700', intensity: 0.8 }
            ]
        },
        {
            id: 'cyber-city',
            name: 'Cyber City',
            description: 'Futuristic neon cityscape',
            environment: {
                preset: 'tron',
                skyType: 'color',
                skyColor: '#000033',
                ground: 'flat',
                groundColor: '#1a1a2e',
                groundTexture: 'squares',
                lighting: 'point',
                fog: 0.8
            },
            objects: [
                { type: 'box', position: { x: 0, y: 2, z: -6 }, scale: { x: 1, y: 4, z: 1 }, color: '#00FFFF', animation: 'pulse' },
                { type: 'box', position: { x: -3, y: 3, z: -8 }, scale: { x: 1, y: 6, z: 1 }, color: '#FF00FF', animation: 'pulse' },
                { type: 'box', position: { x: 3, y: 1.5, z: -7 }, scale: { x: 1, y: 3, z: 1 }, color: '#00FF00', animation: 'pulse' },
                { type: 'box', position: { x: -6, y: 2.5, z: -10 }, scale: { x: 1, y: 5, z: 1 }, color: '#FFFF00' },
                { type: 'box', position: { x: 6, y: 1, z: -9 }, scale: { x: 1, y: 2, z: 1 }, color: '#FF0088' }
            ],
            lights: [
                { type: 'point', position: { x: 0, y: 5, z: -5 }, color: '#00FFFF', intensity: 1.5 },
                { type: 'point', position: { x: -5, y: 3, z: -8 }, color: '#FF00FF', intensity: 1.2 },
                { type: 'point', position: { x: 5, y: 3, z: -8 }, color: '#00FF00', intensity: 1.2 }
            ]
        },
        {
            id: 'forest-glen',
            name: 'Forest Glen',
            description: 'Peaceful forest clearing with trees',
            environment: {
                preset: 'forest',
                skyType: 'gradient',
                skyColor: '#87CEEB',
                horizonColor: '#90EE90',
                ground: 'hills',
                groundColor: '#2D5016',
                groundTexture: 'walkernoise',
                lighting: 'distant',
                lightPosition: { x: 0.5, y: 1, z: -0.5 },
                fog: 0.4,
                dressingAmount: 100
            },
            objects: [
                // Tree trunks
                { type: 'cylinder', position: { x: -4, y: 2, z: -6 }, scale: { x: 0.4, y: 4, z: 0.4 }, color: '#4A2C0F' },
                { type: 'cone', position: { x: -4, y: 5, z: -6 }, scale: { x: 2, y: 3, z: 2 }, color: '#228B22' },
                { type: 'cylinder', position: { x: 4, y: 2.5, z: -7 }, scale: { x: 0.5, y: 5, z: 0.5 }, color: '#654321' },
                { type: 'cone', position: { x: 4, y: 6, z: -7 }, scale: { x: 2.5, y: 3.5, z: 2.5 }, color: '#2E7D32' },
                // Rocks
                { type: 'sphere', position: { x: 2, y: 0.3, z: -4 }, scale: { x: 0.6, y: 0.4, z: 0.6 }, color: '#808080' },
                { type: 'sphere', position: { x: -2, y: 0.4, z: -3 }, scale: { x: 0.8, y: 0.5, z: 0.7 }, color: '#696969' }
            ],
            lights: [
                { type: 'ambient', color: '#404040', intensity: 0.6 }
            ]
        },
        {
            id: 'space-station',
            name: 'Space Station',
            description: 'Floating in space with stars',
            environment: {
                preset: 'starry',
                skyType: 'color',
                skyColor: '#000000',
                ground: 'flat',
                groundColor: '#2C3E50',
                groundTexture: 'squares',
                lighting: 'point',
                fog: 0
            },
            objects: [
                // Station modules
                { type: 'box', position: { x: 0, y: 1.5, z: -5 }, scale: { x: 3, y: 2, z: 2 }, color: '#CCCCCC', animation: 'rotate' },
                { type: 'cylinder', position: { x: -4, y: 1.5, z: -5 }, scale: { x: 0.8, y: 2, z: 0.8 }, color: '#FFFFFF', rotation: { x: 0, y: 0, z: 90 } },
                { type: 'cylinder', position: { x: 4, y: 1.5, z: -5 }, scale: { x: 0.8, y: 2, z: 0.8 }, color: '#FFFFFF', rotation: { x: 0, y: 0, z: 90 } },
                // Solar panels
                { type: 'box', position: { x: -6, y: 1.5, z: -5 }, scale: { x: 0.1, y: 3, z: 2 }, color: '#1E3A8A' },
                { type: 'box', position: { x: 6, y: 1.5, z: -5 }, scale: { x: 0.1, y: 3, z: 2 }, color: '#1E3A8A' },
                // Planets
                { type: 'sphere', position: { x: -10, y: 5, z: -15 }, scale: { x: 3, y: 3, z: 3 }, color: '#FF6B6B' },
                { type: 'sphere', position: { x: 10, y: 3, z: -20 }, scale: { x: 2, y: 2, z: 2 }, color: '#4ECDC4' }
            ],
            lights: [
                { type: 'point', position: { x: 0, y: 5, z: 0 }, color: '#FFFFFF', intensity: 2 },
                { type: 'ambient', color: '#1a1a2e', intensity: 0.3 }
            ]
        },
        {
            id: 'crystal-cave',
            name: 'Crystal Cave',
            description: 'Mystical cave with glowing crystals',
            environment: {
                preset: 'default',
                skyType: 'color',
                skyColor: '#1a1a2e',
                ground: 'flat',
                groundColor: '#2C2C3E',
                groundTexture: 'walkernoise',
                lighting: 'point',
                fog: 0.7
            },
            objects: [
                // Crystals
                { type: 'octahedron', position: { x: -3, y: 2, z: -5 }, scale: { x: 1, y: 2, z: 1 }, color: '#9B59B6', animation: 'pulse' },
                { type: 'octahedron', position: { x: 3, y: 1.5, z: -6 }, scale: { x: 0.8, y: 1.5, z: 0.8 }, color: '#3498DB', animation: 'pulse' },
                { type: 'octahedron', position: { x: 0, y: 2.5, z: -7 }, scale: { x: 1.2, y: 2.5, z: 1.2 }, color: '#1ABC9C', animation: 'pulse' },
                { type: 'octahedron', position: { x: -5, y: 1, z: -8 }, scale: { x: 0.6, y: 1.2, z: 0.6 }, color: '#E74C3C' },
                { type: 'octahedron', position: { x: 5, y: 1.8, z: -7 }, scale: { x: 0.9, y: 1.8, z: 0.9 }, color: '#F39C12' },
                // Rocks
                { type: 'sphere', position: { x: 2, y: 0.5, z: -4 }, scale: { x: 1, y: 0.7, z: 1 }, color: '#34495E' },
                { type: 'sphere', position: { x: -2, y: 0.6, z: -3 }, scale: { x: 1.2, y: 0.8, z: 1.2 }, color: '#2C3E50' }
            ],
            lights: [
                { type: 'point', position: { x: -3, y: 2, z: -5 }, color: '#9B59B6', intensity: 1 },
                { type: 'point', position: { x: 3, y: 1.5, z: -6 }, color: '#3498DB', intensity: 0.8 },
                { type: 'point', position: { x: 0, y: 2.5, z: -7 }, color: '#1ABC9C', intensity: 1.2 }
            ]
        },
        {
            id: 'desert-oasis',
            name: 'Desert Oasis',
            description: 'Sandy desert with palm trees and water',
            environment: {
                preset: 'egypt',
                skyType: 'gradient',
                skyColor: '#87CEEB',
                horizonColor: '#FFE4B5',
                ground: 'flat',
                groundColor: '#F4C542',
                groundTexture: 'walkernoise',
                lighting: 'distant',
                lightPosition: { x: 0, y: 1, z: 0 },
                fog: 0.3
            },
            objects: [
                // Water pool
                { type: 'cylinder', position: { x: 0, y: 0.1, z: -6 }, scale: { x: 4, y: 0.2, z: 4 }, color: '#4A90E2' },
                // Palm trees
                { type: 'cylinder', position: { x: -3, y: 2, z: -8 }, scale: { x: 0.3, y: 4, z: 0.3 }, color: '#8B4513' },
                { type: 'sphere', position: { x: -3, y: 4.5, z: -8 }, scale: { x: 1.5, y: 1.5, z: 1.5 }, color: '#228B22' },
                { type: 'cylinder', position: { x: 3, y: 2.5, z: -7 }, scale: { x: 0.3, y: 5, z: 0.3 }, color: '#A0522D' },
                { type: 'sphere', position: { x: 3, y: 5.5, z: -7 }, scale: { x: 1.8, y: 1.8, z: 1.8 }, color: '#2E7D32' },
                // Pyramids
                { type: 'cone', position: { x: -8, y: 2, z: -15 }, scale: { x: 4, y: 4, z: 4 }, color: '#DEB887', rotation: { x: 0, y: 45, z: 0 } },
                { type: 'cone', position: { x: 8, y: 1.5, z: -12 }, scale: { x: 3, y: 3, z: 3 }, color: '#D2B48C', rotation: { x: 0, y: -30, z: 0 } }
            ],
            lights: [
                { type: 'point', position: { x: 0, y: 10, z: -5 }, color: '#FFE4B5', intensity: 1.5 }
            ]
        },
        {
            id: 'floating-islands',
            name: 'Floating Islands',
            description: 'Magical floating islands in the sky',
            environment: {
                preset: 'default',
                skyType: 'gradient',
                skyColor: '#87CEEB',
                horizonColor: '#B0E0E6',
                ground: 'none',
                lighting: 'distant',
                lightPosition: { x: 1, y: 1, z: 0 },
                fog: 0.5
            },
            objects: [
                // Main platform
                { type: 'cylinder', position: { x: 0, y: 0, z: -5 }, scale: { x: 5, y: 0.5, z: 5 }, color: '#8FBC8F' },
                // Floating islands
                { type: 'sphere', position: { x: -6, y: 3, z: -10 }, scale: { x: 2, y: 1.5, z: 2 }, color: '#90EE90' },
                { type: 'sphere', position: { x: 6, y: 4, z: -12 }, scale: { x: 2.5, y: 2, z: 2.5 }, color: '#98FB98' },
                { type: 'sphere', position: { x: 0, y: 6, z: -15 }, scale: { x: 3, y: 2.5, z: 3 }, color: '#7CFC00' },
                // Trees on islands
                { type: 'cylinder', position: { x: -6, y: 4.5, z: -10 }, scale: { x: 0.2, y: 1, z: 0.2 }, color: '#654321' },
                { type: 'cone', position: { x: -6, y: 5.5, z: -10 }, scale: { x: 0.8, y: 1, z: 0.8 }, color: '#228B22' },
                // Waterfalls
                { type: 'cylinder', position: { x: 3, y: -2, z: -5 }, scale: { x: 0.3, y: 4, z: 0.3 }, color: '#4A90E2', opacity: 0.6 },
                { type: 'cylinder', position: { x: -3, y: -1.5, z: -5 }, scale: { x: 0.3, y: 3, z: 0.3 }, color: '#5DADE2', opacity: 0.6 },
                // Clouds
                { type: 'sphere', position: { x: 4, y: 8, z: -8 }, scale: { x: 2, y: 1, z: 1.5 }, color: '#FFFFFF', opacity: 0.7 },
                { type: 'sphere', position: { x: -5, y: 7, z: -7 }, scale: { x: 1.8, y: 0.9, z: 1.3 }, color: '#F0F8FF', opacity: 0.7 }
            ],
            lights: [
                { type: 'ambient', color: '#FFFFFF', intensity: 0.7 },
                { type: 'point', position: { x: 0, y: 10, z: -10 }, color: '#FFE4B5', intensity: 1 }
            ]
        }
    ],

    /**
     * Get a random world
     */
    getRandomWorld() {
        const randomIndex = Math.floor(Math.random() * this.worlds.length);
        return this.worlds[randomIndex];
    },

    /**
     * Get world by ID
     */
    getWorldById(id) {
        return this.worlds.find(world => world.id === id);
    },

    /**
     * Get all world names
     */
    getWorldNames() {
        return this.worlds.map(world => ({
            id: world.id,
            name: world.name,
            description: world.description
        }));
    }
};
